import { ExproOrderModel } from '@/models';
import { AppListenerEntry } from '@/redux';
import {
  getPayModes,
  managePayExproOrderResponse,
  payExproOrder,
  setExproOrder,
  setPayModes,
  setValidateSelectedAddress,
  updateExproOrder,
  updateExproOrderAddress,
  updateExproOrderPaymentMode
} from '@/redux/slices/expro.slice';
import { hidePageProgress, showPageProgress } from '@/redux/slices/gui.slice';
import { addApiErrorToast, addToast } from '@/redux/slices/toast.slice';
import { getErrorMessageByKey } from '@/utils/millesimaUtils';
import { PAGES } from '@/constants/pages';
import { PAYMENT_STATES } from '@/constants/checkout';
import { STORE_ID_DEFAULT } from '@/constants/stores';

export const epicUpdateExproOrderAddress: AppListenerEntry = {
  actionCreator: updateExproOrderAddress,
  effect: (_action: ReturnType<typeof updateExproOrderAddress>, { dispatch }) => {
    dispatch(showPageProgress());
    dispatch(setValidateSelectedAddress({ validateSelectedAddress: false }));
    dispatch(hidePageProgress());
  }
};

export const epicUpdateExproOrderPaymentMode: AppListenerEntry = {
  actionCreator: updateExproOrderPaymentMode,
  effect: async (action: ReturnType<typeof updateExproOrderPaymentMode>, { getState, dispatch, extra: { exproService } }) => {
    try {
      const exproOrder = getState().expro.exproOrder;
      dispatch(showPageProgress());
      await exproService.updateExproOrder({
        ...exproOrder,
        paymentMode: action.payload.payMode
      });
    } catch (error) {
      dispatch(addApiErrorToast({ error }));
    } finally {
      dispatch(hidePageProgress());
    }
  }
};
export const epicUpdateExproOrder: AppListenerEntry = {
  actionCreator: updateExproOrder,
  effect: async ({ payload }: ReturnType<typeof updateExproOrder>, { dispatch, extra: { exproService } }) => {
    try {
      dispatch(showPageProgress());
      const updatedExproOrder = await exproService.updateExproOrder(payload.exproOrder) as ExproOrderModel;
      dispatch(setExproOrder({ exproOrder: updatedExproOrder }));
      if (payload?.updateStep) {
        payload.updateStep();
      }
    } catch (error) {
      dispatch(addApiErrorToast({ error }));
    } finally {
      dispatch(hidePageProgress());
    }
  }
};

export const epicGetPayModes: AppListenerEntry = {
  actionCreator: getPayModes,
  effect: async ({ payload }: ReturnType<typeof getPayModes>, { dispatch, getState, extra: { exproService } }) => {
    const { storeId, isApplePayEnabled } = payload;
    const storeIdForPrice = getState().gui.storeIdForPrice;

    try {
      const payModes = await exproService.getPayModes(storeIdForPrice || storeId, isApplePayEnabled) as any[];
      dispatch(setPayModes({ payModes }));
    } catch (error) {
      dispatch(addApiErrorToast({ error }));
    }
  }
};

export const epicPayExproOrder: AppListenerEntry = {
  actionCreator: payExproOrder,
  effect: async ({ payload }: ReturnType<typeof payExproOrder>, { getState, dispatch, extra: { exproService } }) => {
    const { processExproOrderRequest, t } = payload;
    const { storeId = STORE_ID_DEFAULT } = getState().root.storeConf;

    try {
      dispatch(showPageProgress());

      let response: any = null;
      response = await exproService.payExproOrder(processExproOrderRequest, storeId) as any[];
      const { state, reason, forwardUrl, adyenAction, paymentAction } = response;

      dispatch(managePayExproOrderResponse({ response: { state, reason, forwardUrl, adyenAction, paymentAction }, t }));
    } catch (error) {
      dispatch(addApiErrorToast({ error }));
      dispatch(hidePageProgress());
    }
  }
};

export const epicManagePayExproOrderResponse: AppListenerEntry = {
  actionCreator: managePayExproOrderResponse,
  effect: async ({ payload }: ReturnType<typeof managePayExproOrderResponse>, { getState, dispatch }) => {
    const { response, t } = payload;
    const { url } = getState().root.storeConf;

    switch (response.state) {
      case PAYMENT_STATES.DECLINED:
      case PAYMENT_STATES.ERROR:
        const paymentAction = response.paymentAction;
        dispatch(hidePageProgress());
        dispatch(addToast({ msg: getErrorMessageByKey(t, `errors.${paymentAction}.${response?.reason?.code}`, 'errors.payment.generic') }));
        window.location.reload();
        break;

      case PAYMENT_STATES.COMPLETED:
      case PAYMENT_STATES.PENDING:
        // redirect to report page
        window.location.href = `${url}${PAGES.EXPRO_ORDER_REPORT}`;
        break;

      case PAYMENT_STATES.FORWARDING:
        if (response?.forwardUrl) {
          // Do redirection => quit application right now
          window.location.href = response.forwardUrl;
        }
        break;

      default:
        dispatch(addToast({ msg: getErrorMessageByKey(t, 'errors.payment.generic') }));
        break;
    }
  }
};
